import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import {
  CheckCircle2,
  Layers,
  Loader2,
  ShieldCheck,
  Sparkles,
  Tag,
  X,
} from "lucide-react";
import { createPlatformItem, PLATFORM_LIST_ID } from "../api/graph";
import type { NewPlatformFields } from "../api/graph";
import { useGraphToken } from "../auth/useGraphToken";
import { useListColumns, choicesFor, hintFor } from "../hooks/useListColumns";
import type { PlatformItem } from "../types/navigator";
import { ConfirmDialog } from "./ConfirmDialog";

type Props = {
  /** Existing platform items — kept for API compatibility with the caller.
   *  Choice fields now use the list's fixed vocabularies, so we no longer
   *  derive options from prior entries. */
  existing: PlatformItem[];
  onClose: () => void;
  onCreated: () => void;
};

type FormState = {
  Title: string;
  Purpose: string;
  Notes: string;
  PlatformType: string;
  LicenseType: string;
  Complexity: string;
  UsedbybroaderBrookfield: string;
  // Approved Users + Cost Model are multi-choice columns — track selections as
  // arrays of labels so they round-trip to SharePoint as arrays.
  ApprovedUse: string[];
  CostModel: string[];
};

const EMPTY: FormState = {
  Title: "",
  Purpose: "",
  Notes: "",
  PlatformType: "",
  LicenseType: "",
  Complexity: "",
  UsedbybroaderBrookfield: "",
  ApprovedUse: [],
  CostModel: [],
};

// New submissions land as "New" — the Platform list's real default status
// ("Pending" is not a valid choice on this list's Status column).
const LOCKED_STATUS = "New";
const AUTO_CLOSE_MS = 2400;

// Fallback choice values, used only until the live column definitions load
// from SharePoint (or if that fetch fails). All of these are closed choice
// sets (allowTextEntry: false), so free text would be the wrong value to
// store — render them as real dropdowns instead.
const PLATFORM_TYPE_CHOICES_FALLBACK = [
  "Website/Agent",
  "Desktop (requires install)",
  "Mobile",
  "API/MCP",
  "CLI/Terminal",
  "IDE/Plugin/Extension",
];

const LICENSE_TYPE_CHOICES_FALLBACK = ["Enterprise", "Team", "Individual / Pro", "Free"];

const COMPLEXITY_CHOICES_FALLBACK = ["Beginner", "Intermediate", "Advanced"];

const BROADER_CHOICES_FALLBACK = ["Yes", "No"];

// Multi-choice ("checkBoxes") columns — selections are stored as arrays.
const APPROVED_USE_CHOICES_FALLBACK = ["Everyone", "IT", "Citizen Developers"];

const COST_MODEL_CHOICES_FALLBACK = [
  "Per Named User",
  "Per Concurrent User",
  "Per Device",
  "Per Token",
  "Per Agent/Bot",
  "Per Tenant",
];

export function AddPlatformItemModal({ onClose, onCreated }: Props) {
  const getToken = useGraphToken();
  const { columns } = useListColumns(PLATFORM_LIST_ID);
  const platformTypeChoices = useMemo(
    () => choicesFor(columns, "PlatformType", PLATFORM_TYPE_CHOICES_FALLBACK),
    [columns]
  );
  const licenseTypeChoices = useMemo(
    () => choicesFor(columns, "LicenseType", LICENSE_TYPE_CHOICES_FALLBACK),
    [columns]
  );
  const complexityChoices = useMemo(
    () => choicesFor(columns, "Complexity", COMPLEXITY_CHOICES_FALLBACK),
    [columns]
  );
  const broaderChoices = useMemo(
    () => choicesFor(columns, "UsedbybroaderBrookfield", BROADER_CHOICES_FALLBACK),
    [columns]
  );
  const approvedUseChoices = useMemo(
    () => choicesFor(columns, "ApprovedUse", APPROVED_USE_CHOICES_FALLBACK),
    [columns]
  );
  const costModelChoices = useMemo(
    () => choicesFor(columns, "CostModel", COST_MODEL_CHOICES_FALLBACK),
    [columns]
  );
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Unsaved work exists while the form differs from its empty starting state
  // and hasn't been submitted yet. We compare a serialized snapshot so any
  // typed field — including the checkbox arrays — counts.
  const dirty = !submitted && JSON.stringify(form) !== JSON.stringify(EMPTY);

  // Guarded dismiss: prompt before discarding unsaved edits. A save in progress
  // blocks dismissal entirely (matches the existing behavior).
  const requestClose = useCallback(() => {
    if (submitting) return;
    if (dirty) setConfirmOpen(true);
    else onClose();
  }, [submitting, dirty, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting && !confirmOpen) requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose, submitting, confirmOpen]);

  useEffect(() => {
    if (!submitted) return;
    const t = window.setTimeout(() => onCreated(), AUTO_CLOSE_MS);
    return () => window.clearTimeout(t);
  }, [submitted, onCreated]);

  const update = (key: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleMulti = (key: "ApprovedUse" | "CostModel", value: string) =>
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((v) => v !== value)
        : [...prev[key], value],
    }));

  const canSubmit =
    form.Title.trim().length > 0 &&
    form.Purpose.trim().length > 0 &&
    form.PlatformType.trim().length > 0 &&
    !submitting;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const token = await getToken();
      const payload: NewPlatformFields = {
        Title: form.Title.trim(),
        Purpose: form.Purpose.trim() || undefined,
        Notes: form.Notes.trim() || undefined,
        PlatformType: form.PlatformType.trim() || undefined,
        LicenseType: form.LicenseType.trim() || undefined,
        Complexity: form.Complexity.trim() || undefined,
        UsedbybroaderBrookfield:
          form.UsedbybroaderBrookfield.trim() || undefined,
        ApprovedUse: form.ApprovedUse.length ? form.ApprovedUse : undefined,
        CostModel: form.CostModel.length ? form.CostModel : undefined,
        Status: LOCKED_STATUS,
      };
      await createPlatformItem(token, payload);
      setSubmitting(false);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="modal-backdrop"
        onClick={(e) => {
          if (e.target === e.currentTarget && !submitting) requestClose();
        }}
      >
        <div
          className="modal modal-platform add-item-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-accent" aria-hidden />
          <div className="modal-head">
            <div className="modal-head-icon icon-platform" aria-hidden>
              <Layers size={22} />
            </div>
            <div className="modal-head-text">
              <span className="modal-kind">AI Platform</span>
              <h2 className="modal-title">
                {submitted ? "Submission received" : "Add new platform"}
              </h2>
              {!submitted ? (
                <p className="add-item-subtitle">
                  Tell us about the platform. Entries are added as{" "}
                  <strong>New</strong> and published after review.
                </p>
              ) : null}
            </div>
            {!submitted ? (
              <button
                type="button"
                className="modal-close"
                onClick={requestClose}
                disabled={submitting}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            ) : null}
          </div>

          {submitted ? (
            <div className="add-item-success" role="status" aria-live="polite">
              <div className="add-item-success-icon" aria-hidden>
                <CheckCircle2 size={40} strokeWidth={2.2} />
              </div>
              <h3 className="add-item-success-title">
                Thanks — your entry is in the queue
              </h3>
              <p className="add-item-success-body">
                Your platform submission was saved successfully. A reviewer will
                check it over and publish it to AI Platforms shortly.
              </p>
              <button
                type="button"
                className="btn btn-primary add-item-success-done"
                onClick={onCreated}
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <form className="modal-body add-item-form" onSubmit={onSubmit}>
                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <Sparkles size={14} aria-hidden />
                    <h3>The basics</h3>
                  </header>
                  <p className="add-item-section-sub">
                    Give the platform a clear name and explain what it does.
                  </p>

                  <Field
                    label="Title"
                    required
                    hint="The platform name as people refer to it. Example: “Microsoft 365 Copilot”."
                  >
                    <input
                      type="text"
                      value={form.Title}
                      onChange={(e) => update("Title", e.target.value)}
                      required
                      maxLength={255}
                      autoFocus
                      placeholder={hintFor(columns, "Title", "What is this platform called?")}
                    />
                  </Field>

                  <Field
                    label="What it is"
                    required
                    hint="One or two sentences on what the platform is for and the problems it helps solve."
                  >
                    <textarea
                      rows={3}
                      value={form.Purpose}
                      onChange={(e) => update("Purpose", e.target.value)}
                      placeholder={hintFor(columns, "Purpose", "What does this platform do, and when should someone reach for it?")}
                    />
                  </Field>

                  <Field
                    label="Key capabilities"
                    hint="Notable features, integrations, or strengths. Bullet-style notes are fine."
                  >
                    <textarea
                      rows={3}
                      value={form.Notes}
                      onChange={(e) => update("Notes", e.target.value)}
                      placeholder={hintFor(columns, "Notes", "What can it actually do? Anything reviewers should know.")}
                    />
                  </Field>
                </section>

                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <Tag size={14} aria-hidden />
                    <h3>Classify it</h3>
                  </header>
                  <p className="add-item-section-sub">
                    These tags power filters and search. Pick the closest match.
                  </p>

                  <div className="add-item-row">
                    <Field
                      label="Platform type"
                      required
                      hint="Where does the platform live?"
                    >
                      <ChoiceSelect
                        value={form.PlatformType}
                        onChange={(v) => update("PlatformType", v)}
                        options={platformTypeChoices}
                        placeholder="Select a platform type…"
                        required
                      />
                    </Field>
                    <Field
                      label="Plan type"
                      hint="The plan established with the vendor. Enterprise, Team and Individual / Pro are paid."
                    >
                      <ChoiceSelect
                        value={form.LicenseType}
                        onChange={(v) => update("LicenseType", v)}
                        options={licenseTypeChoices}
                        placeholder="Select a plan type…"
                      />
                    </Field>
                  </div>

                  <div className="add-item-row">
                    <Field
                      label="Skill level"
                      hint="Roughly how hard is it for someone to get value?"
                    >
                      <ChoiceSelect
                        value={form.Complexity}
                        onChange={(v) => update("Complexity", v)}
                        options={complexityChoices}
                        placeholder="Select a skill level…"
                      />
                    </Field>
                    <Field
                      label="Used by BAM"
                      hint="Is it used across the broader Brookfield, or just Residential?"
                    >
                      <ChoiceSelect
                        value={form.UsedbybroaderBrookfield}
                        onChange={(v) => update("UsedbybroaderBrookfield", v)}
                        options={broaderChoices}
                        placeholder="Select one…"
                      />
                    </Field>
                  </div>

                  <Field
                    label="Billing / cost model"
                    hint="How is the service paid for? Select all that apply."
                  >
                    <CheckGroup
                      values={form.CostModel}
                      options={costModelChoices}
                      onToggle={(v) => toggleMulti("CostModel", v)}
                      ariaLabel="Billing / cost model"
                    />
                  </Field>
                </section>

                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <ShieldCheck size={14} aria-hidden />
                    <h3>Approval & governance</h3>
                  </header>
                  <p className="add-item-section-sub">
                    Help users understand where this platform can safely be used.
                  </p>

                  <Field
                    label="Approved users"
                    hint="Which audiences are approved to use the platform? Select all that apply."
                  >
                    <CheckGroup
                      values={form.ApprovedUse}
                      options={approvedUseChoices}
                      onToggle={(v) => toggleMulti("ApprovedUse", v)}
                      ariaLabel="Approved users"
                    />
                  </Field>
                </section>

                {error ? (
                  <div className="add-item-error" role="alert">
                    <strong>Couldn't save.</strong> {error}
                  </div>
                ) : null}
              </form>

              <div className="modal-foot">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!canSubmit}
                  onClick={onSubmit}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="add-item-spin" /> Saving…
                    </>
                  ) : (
                    "Submit for review"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Discard your changes?"
        message="You've started filling this out but haven't submitted yet. If you leave now, your entries will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        tone="danger"
        autoFocusConfirm={false}
        onConfirm={() => {
          setConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="add-item-field">
      <span className="add-item-label">
        {label}
        {required ? (
          <span aria-hidden className="add-item-required">
            *
          </span>
        ) : null}
      </span>
      {children}
      {hint ? <span className="add-item-hint">{hint}</span> : null}
    </label>
  );
}

function ChoiceSelect({
  value,
  onChange,
  options,
  placeholder,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
    >
      <option value="" disabled>
        {placeholder ?? "Select…"}
      </option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

function CheckGroup({
  values,
  options,
  onToggle,
  ariaLabel,
}: {
  values: string[];
  options: string[];
  onToggle: (v: string) => void;
  ariaLabel: string;
}) {
  return (
    <div
      className="add-item-checkgroup"
      role="group"
      aria-label={ariaLabel}
      style={{ display: "flex", flexDirection: "column" }}
    >
      {options.map((opt) => {
        const checked = values.includes(opt);
        return (
          <label
            key={opt}
            className={`add-item-check${checked ? " is-checked" : ""}`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(opt)}
            />
            <span>{opt}</span>
          </label>
        );
      })}
    </div>
  );
}
